/**
 * Copy and structure shared by both landing-page types.
 *
 * Lives here rather than in the JSON data files because none of it varies by
 * canton or category - it is the same on every page, so it is code, not content.
 */

// Imported rather than referenced by URL so webpack fingerprints them, which
// lets them be cached immutably - the old Express /assets mount was explicitly
// capped at one day because the filenames carried no hash.
import imgLocation from './assets/Kategorie_Location.webp';
import imgCatering from './assets/Kategorie_Catering.webp';
import imgFotoVideo from './assets/Kategorie_FotoVideo.webp';
import imgMusik from './assets/Kategorie_Musik.webp';
import imgDekoration from './assets/Kategorie_Dekoration.webp';
import imgMakeup from './assets/Kategorie_Makeup.webp';
import imgDienstleister from './assets/Kategorie_Dienstleister.webp';
import imgEventmaterial from './assets/Kategorie_Eventmaterial.webp';
import imgTransport from './assets/Kategorie_Transport.webp';

/**
 * The nine categories, in the order the /kategorie/ hub lists them: roughly the
 * order couples book.
 */
export const CATEGORIES = [
  { slug: 'location', label: 'Location', image: imgLocation },
  { slug: 'catering', label: 'Catering', image: imgCatering },
  { slug: 'fotograf', label: 'Foto und Video', image: imgFotoVideo },
  { slug: 'musik', label: 'Musik und Unterhaltung', image: imgMusik },
  { slug: 'dekoration', label: 'Dekoration', image: imgDekoration },
  { slug: 'beauty', label: 'Beauty und Styling', image: imgMakeup },
  { slug: 'planer', label: 'Planer und Dienstleister', image: imgDienstleister },
  { slug: 'eventmaterial', label: 'Eventmaterial', image: imgEventmaterial },
  { slug: 'transport', label: 'Transport', image: imgTransport },
];

/** The three steps, identical on every landing page. */
export const STEPS = {
  heading: 'So bucht ihr auf Toastably',
  text: 'Drei einfache Schritte – von der ersten Anfrage bis zur fixen Buchung.',
  items: [
    {
      title: 'Anbieter:innen finden',
      text: 'Verfügbare Dienstleister:innen mit klaren Preisen und Leistungen.',
    },
    { title: 'Vergleichen', text: 'Alle Angebote übersichtlich an einem Ort. Kein E-Mail-Chaos.' },
    {
      title: 'Direkt buchen',
      text: 'Fixe Bestätigung mit sicherer Bezahlung über die Plattform.',
    },
  ],
};

/** FAQ entries that hold for every canton, appended after any canton-specific ones. */
export const SHARED_REGION_FAQ = [
  {
    q: 'Wie finde ich Anbieter:innen, die in meiner Region arbeiten?',
    a:
      'Jede Anbieter:in auf Toastably gibt an, in welchen Kantonen sie arbeitet. Diese Seite zeigt alle, die den ausgewählten Kanton abdecken – unabhängig davon, wo sie ihren Sitz haben.',
  },
  {
    q: 'Kostet die Nutzung von Toastably etwas?',
    a:
      'Für suchende Paare ist Toastably kostenlos. Es fallen keine Plattformgebühren an, ihr bezahlt nur die gebuchte Leistung.',
  },
  {
    q: 'Arbeiten die Anbieter:innen auch ausserhalb ihres Kantons?',
    a:
      'Viele arbeiten in mehreren Kantonen und reisen für eure Hochzeit an. Die Kantonsliste auf dem jeweiligen Angebot zeigt genau, welche Regionen abgedeckt sind.',
  },
];

// "im Kanton <X>" rather than "in <X>": several cantons take an article in
// German (im Aargau, im Jura, im Tessin, im Wallis, im Thurgau, in der Waadt),
// so a bare "in <label>" is ungrammatical for six of the 26.
export const defaultHeading = canton => `Hochzeitsdienstleister im Kanton ${canton.label}`;

export const defaultIntro = canton =>
  `Findet Hochzeitsdienstleister:innen, die im Kanton ${canton.label} arbeiten – von der Location über Catering und Fotografie bis zu Musik und Transport. Alle Angebote mit klaren Preisen, direkt vergleichbar und online buchbar.`;

export const defaultMetaDescription = canton =>
  `Hochzeitsdienstleister im Kanton ${canton.label} vergleichen: Location, Catering, Fotograf:in, Musik und mehr. Transparente Preise, direkt online buchbar.`;

export const defaultPageTitle = canton =>
  `Hochzeitsdienstleister ${canton.label} – Location, Catering, Foto vergleichen | Toastably`;
